// Speil av functions/aiAdvisor/aiAdvisorNarrative.ts — lokal fallback nar backend
// ikke er tilgjengelig. Kroppen skal vaere identisk; parity-testen i
// functions/aiAdvisor/aiAdvisorNarrative.test.ts vokter det.
const PlummeAiAdvisorNarrative = (() => {
  // ============================================================
  // AI Radgiver — narrativlaget (dom, forklaring, grep, konfidens, overvaking)
  // ============================================================
  // Alt her er avledet av bokforte tall. Ingen tekst returneres: hver post er
  // { key, vars } slik at frontend oversetter via t(). Da slipper vi norsk tekst
  // ut av regnskapsdomenet, og engelsk UI far riktig sprak ogsa i narrativet.
  //
  // Regel for `gain`: beloepet skal alltid vaere et tall brukeren kan peke pa i
  // regnskapet — forfalt belop, MVA som forfaller, eller faktisk kostnadsokning.
  // Ingen anslatte besparelser.

  const num = (value, fallback = 0) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }
  const safeArray = (value) => (Array.isArray(value) ? value : [])
  const upcomingForecast = (forecast) => safeArray(forecast)
    .filter((point) => point.certainty !== 'confirmed')

  // Kostnadsokningen i kroner, regnet tilbake fra prosentendringen modellen alt har.
  const costIncrease = (amount, changePercent) => {
    const change = num(changePercent)
    if (change <= 0) return 0
    const previous = num(amount) / (1 + change / 100)
    return Math.max(0, Math.round(num(amount) - previous))
  }

  const lowestForecastBalance = (forecast) => upcomingForecast(forecast)
    .reduce((lowest, point) => (lowest == null || num(point.balance) < num(lowest.balance) ? point : lowest), null)

  // ---------- Dom per fane ----------
  const liquidityVerdict = ({ forecast, buffer, salesOverdue, freeCapital }) => {
    const upcoming = upcomingForecast(forecast)
    const dip = upcoming.find((point) => num(point.balance) < num(buffer))
    if (dip) return { tone: 'red', key: 'liquidityBufferBreach', vars: { month: dip.m, buffer: Math.round(num(buffer)), shortfall: Math.round(num(buffer) - num(dip.balance)) } }
    if (salesOverdue > 0) return { tone: 'amber', key: 'liquidityOverdue', vars: { amount: Math.round(salesOverdue) } }
    if (!upcoming.length) return { tone: 'amber', key: 'liquidityNoForwardData', vars: {} }
    return { tone: 'green', key: 'liquidityOk', vars: { amount: Math.round(num(freeCapital)) } }
  }
  const costVerdict = ({ costs, costsYoy, flagged }) => {
    if (flagged) return { tone: 'amber', key: 'costSpike', vars: { name: flagged.name, pct: Math.round(num(flagged.chg)) } }
    if (costsYoy == null) return { tone: 'green', key: 'costNoComparison', vars: { amount: Math.round(num(costs)) } }
    if (costsYoy > 5) return { tone: 'amber', key: 'costUp', vars: { pct: Math.round(costsYoy) } }
    if (costsYoy < -5) return { tone: 'green', key: 'costDown', vars: { pct: Math.abs(Math.round(costsYoy)) } }
    return { tone: 'green', key: 'costFlat', vars: { amount: Math.round(num(costs)) } }
  }
  const taxVerdict = ({ vatDue, taxBuffer }) => {
    if (vatDue > 0 && taxBuffer > 0) return { tone: 'amber', key: 'taxBoth', vars: { vat: Math.round(vatDue), buffer: Math.round(taxBuffer) } }
    if (vatDue > 0) return { tone: 'amber', key: 'taxVat', vars: { vat: Math.round(vatDue) } }
    if (taxBuffer > 0) return { tone: 'amber', key: 'taxBuffer', vars: { buffer: Math.round(taxBuffer) } }
    return { tone: 'green', key: 'taxNothingDue', vars: {} }
  }
  const reportVerdict = ({ income, costs, result }) => ({
    tone: result >= 0 ? 'green' : 'red',
    key: result >= 0 ? 'reportProfit' : 'reportLoss',
    vars: { income: Math.round(income), costs: Math.round(costs), result: Math.round(Math.abs(result)) },
  })
  const benchmarkVerdict = ({ bench }) => {
    const rows = safeArray(bench)
    if (!rows.length) return { tone: 'green', key: 'benchNoData', vars: {} }
    const better = rows.filter((row) => (row.better === 'high' ? num(row.you) >= num(row.avg) : num(row.you) <= num(row.avg))).length
    const tone = better === rows.length ? 'green' : better === 0 ? 'red' : 'amber'
    const key = better === rows.length ? 'benchAllStronger' : better === 0 ? 'benchAllWeaker' : 'benchMixed'
    return { tone, key, vars: { better, total: rows.length } }
  }

  // ---------- Hvorfor skjer det ----------
  const liquidityWhy = ({ salesOverdue, purchasesOverdue, vatDue, taxBuffer }) => {
    const rows = []
    if (salesOverdue > 0) rows.push({ key: 'whyOverdueSales', vars: {}, amount: Math.round(salesOverdue), tone: 'red' })
    if (purchasesOverdue > 0) rows.push({ key: 'whyOverduePurchases', vars: {}, amount: Math.round(purchasesOverdue), tone: 'amber' })
    if (vatDue + taxBuffer > 0) rows.push({ key: 'whyPublicDues', vars: {}, amount: Math.round(vatDue + taxBuffer), tone: 'amber' })
    return rows.slice(0, 3)
  }
  const liquidityWatch = ({ forecast, buffer, salesCurrent, purchasesCurrent }) => {
    const lowest = lowestForecastBalance(forecast)
    const gap = lowest ? Math.round(num(lowest.balance) - num(buffer)) : null
    const rows = [{
      key: gap == null ? 'watchNoBalance' : gap < 0 ? 'watchBelowBuffer' : 'watchAboveBuffer',
      vars: lowest ? { month: lowest.m } : {},
      amount: gap == null ? null : Math.abs(gap),
      tone: gap == null ? 'amber' : gap < 0 ? 'red' : 'green',
    }]
    if (salesCurrent > 0) rows.push({ key: 'watchReceivables', vars: {}, amount: Math.round(salesCurrent), tone: 'green' })
    if (purchasesCurrent > 0) rows.push({ key: 'watchPayables', vars: {}, amount: Math.round(purchasesCurrent), tone: 'amber' })
    return rows
  }
  const costWhy = ({ costs }) => safeArray(costs)
    .filter((cost) => num(cost.amount) > 0)
    .map((cost) => ({ cost, increase: costIncrease(cost.amount, cost.chg) }))
    .sort((a, b) => b.increase - a.increase || num(b.cost.amount) - num(a.cost.amount))
    .slice(0, 3)
    .map(({ cost, increase }) => (increase > 0
      ? { key: 'whyCostUp', vars: { name: cost.name, pct: Math.round(num(cost.chg)) }, amount: increase, tone: cost.flag ? 'red' : 'amber' }
      : { key: 'whyCostLargest', vars: { name: cost.name }, amount: Math.round(num(cost.amount)), tone: 'green' }))

  // ---------- Hva ville jeg gjort ----------
  // `simulate.deltaBalance` er en FORTEGNSSATT endring i kontantbeholdning, og settes
  // bare der tiltaket faktisk flytter penger inn eller ut av selskapet.
  // Bevisst uten simulate:
  //  - moveSetAsideVat: a sette av mva flytter penger mellom egne kontoer. Samlet
  //    kontantbeholdning er uendret, sa a legge +vatDue pa kurven ville vaert feil.
  //  - moveBuildBuffer: `gain` er avstanden opp til bufferen, altsa et mal — ikke
  //    effekten av en handling.
  const offerFollowupMoves = (invoices, now, year) => {
    const day = 86400000
    const active = safeArray(invoices).filter((entry) => !['annulled', 'deleted', 'reversed'].includes(entry?.status) && !entry?.archivedAt)
    const linkedInvoices = new Set(active.filter((entry) => entry.kind === 'invoice' && entry.convertedFromInvoiceId)
      .map((entry) => String(entry.convertedFromInvoiceId)))
    const inYear = (entry) => String(entry.date || entry.createdAt || entry.offerAcceptedAt || entry.sentAt || '').slice(0, 4) === String(year)
    const offers = active.filter((entry) => entry.kind === 'offer' && inYear(entry))
    const date = (value) => { const parsed = Date.parse(String(value || '')); return Number.isFinite(parsed) ? parsed : null }
    const accepted = offers.filter((entry) => entry.offerAcceptedAt && !linkedInvoices.has(String(entry.id)))
    const offerDrafts = offers.filter((entry) => entry.status === 'draft' && !entry.offerAcceptedAt)
    const unsent = offers.filter((entry) => entry.status !== 'draft' && !entry.sentAt && !entry.offerAcceptedAt)
    const awaiting = offers.filter((entry) => {
      if (!entry.sentAt || entry.offerAcceptedAt) return false
      const sent = date(entry.sentAt)
      const expiry = date(entry.validUntil)
      if (sent == null || sent > now || now - sent > 180 * day) return false
      return now - sent >= 14 * day || (expiry != null && expiry <= now + 7 * day)
    })
    const drafts = active.filter((entry) => entry.kind === 'invoice' && entry.status === 'draft' && inYear(entry))
      .filter((entry) => { const created = date(entry.createdAt || entry.date); return created != null && now - created >= 7 * day })
    return [
      ['moveAcceptedOffer', accepted, 'fileText'],
      ['moveFollowUpOffer', awaiting, 'clock'],
      ['moveSendOffer', unsent, 'send'],
      ['moveOfferDrafts', offerDrafts, 'fileText'],
      ['moveInvoiceDrafts', drafts, 'fileText'],
    ].filter(([, entries]) => entries.length).map(([key, entries, icon]) => ({
      key, vars: { count: entries.length }, count: entries.length, icon, route: 'fakturaliste', gain: null,
    }))
  }

  const liquidityMoves = ({ salesOverdue, purchasesOverdue, vatDue, forecast, buffer, invoices, now, year }) => {
    const moves = []
    if (salesOverdue > 0) {
      moves.push({ key: 'moveChaseOverdue', vars: {}, gain: Math.round(salesOverdue), gainNote: 'gainCashflow', icon: 'clock', route: 'fakturaliste', simulate: { deltaBalance: Math.round(salesOverdue) } })
    }
    if (vatDue > 0) {
      moves.push({ key: 'moveSetAsideVat', vars: {}, gain: Math.round(vatDue), gainNote: 'gainAvoidInterest', icon: 'percent', route: 'innsendinger' })
    }
    const lowest = lowestForecastBalance(forecast)
    if (lowest && num(lowest.balance) < num(buffer)) {
      moves.push({ key: 'moveBuildBuffer', vars: { month: lowest.m }, gain: Math.round(num(buffer) - num(lowest.balance)), gainNote: 'gainToBuffer', icon: 'wallet', route: 'bank' })
    }
    if (purchasesOverdue > 0 && moves.length < 3) {
      // Negativ: a betale forfalte leverandorfakturaer tapper kontoen. Det er riktig
      // a gjore, men kurven skal vise at den gar ned, ikke opp.
      moves.push({ key: 'movePayOverdue', vars: {}, gain: Math.round(purchasesOverdue), gainNote: 'gainAvoidFees', icon: 'bag', route: 'leverandorer', simulate: { deltaBalance: -Math.round(purchasesOverdue) } })
    }
    const cashMoves = moves.slice(0, 3)
    return [...cashMoves.slice(0, 1), ...offerFollowupMoves(invoices, now, year), ...cashMoves.slice(1)]
  }
  const costMoves = ({ costs, missingAttachmentCount }) => {
    const moves = safeArray(costs)
      .map((cost) => ({ cost, increase: costIncrease(cost.amount, cost.chg) }))
      .filter(({ increase }) => increase > 0)
      .sort((a, b) => b.increase - a.increase)
      .slice(0, 2)
      .map(({ cost, increase }) => ({
        key: 'moveReviewCost',
        vars: { name: cost.name },
        gain: increase,
        gainNote: 'gainCostIncrease',
        icon: cost.icon || 'receipt',
        route: 'allebilag',
      }))
    if (missingAttachmentCount > 0) {
      moves.push({ key: 'moveFixVouchers', vars: { count: missingAttachmentCount }, gain: null, gainNote: 'gainDeductionRisk', icon: 'receipt', route: 'allebilag' })
    }
    if (!moves.length) {
      moves.push({ key: 'moveWatchCosts', vars: {}, gain: null, gainNote: 'gainNoActionNeeded', icon: 'check', route: 'resultatregnskap' })
    }
    return moves.slice(0, 3)
  }

  // ---------- Hvor sikker er jeg ----------
  // Kappet pa 90 %: modellen ser bare bokforte tall i Plumme, aldri hele bildet.
  const confidenceFrom = ({ income, costs, previousIncome, previousYearAvailable, concentration, receivables, payables, missingAttachmentCount, voucherCoverageKnown }) => {
    const has = []
    const missing = []
    const check = (condition, key) => (condition ? has : missing).push(key)
    check(income > 0, 'postedIncome')
    check(costs > 0, 'postedCosts')
    check(receivables > 0 || payables > 0, 'openItems')
    check(previousYearAvailable ?? (previousIncome > 0), 'previousYear')
    check(concentration != null, 'customerSplit')
    if (voucherCoverageKnown) check(missingAttachmentCount === 0, 'completeVouchers')
    else missing.push('importedVoucherDetails')
    const total = has.length + missing.length
    const pct = total ? Math.min(90, Math.round((has.length / total) * 100)) : 0
    // `level` er det UI viser. Prosenten gir en presisjon modellen ikke har dekning
    // for — den ser bare det som er bokfort i Plumme — sa den beholdes kun som
    // sorterings-/terskelgrunnlag her.
    const level = total === 0 ? 'thin' : has.length / total >= 0.75 ? 'good' : has.length / total >= 0.4 ? 'medium' : 'thin'
    return { pct, level, has, missing }
  }

  // ---------- Hva SuperPlumma overvaker ----------
  const monitorFrom = ({ forecast, buffer, salesOverdue, purchasesOverdue, vatDue, missingAttachmentCount, concentration }) => [
    { key: 'monitorLiquidity', status: upcomingForecast(forecast).some((point) => num(point.balance) < num(buffer)) ? 'warn' : 'ok' },
    { key: 'monitorOverdueSales', status: salesOverdue > 0 ? 'warn' : 'ok' },
    { key: 'monitorOverduePurchases', status: purchasesOverdue > 0 ? 'warn' : 'ok' },
    { key: 'monitorVat', status: vatDue > 0 ? 'warn' : 'ok' },
    { key: 'monitorVouchers', status: missingAttachmentCount > 0 ? 'warn' : 'ok' },
    { key: 'monitorConcentration', status: concentration != null && concentration > 40 ? 'warn' : 'ok' },
  ]

  // ---------- Hvorfor-punkter pa anbefalingskortene ----------
  const recommendationWhy = ({ recommendations, forecast, buffer, costsYoy, missingAttachmentCount }) => {
    const dips = upcomingForecast(forecast).some((point) => num(point.balance) < num(buffer))
    return safeArray(recommendations).map((recommendation) => {
      const why = []
      if (recommendation.route === 'fakturaliste') {
        why.push({ key: 'recWhyOverdueCash', vars: {} })
        if (dips) why.push({ key: 'recWhyOverdueBuffer', vars: {} })
      }
      if (recommendation.route === 'innsendinger') {
        why.push({ key: 'recWhyVatDeadline', vars: {} })
        why.push({ key: 'recWhyVatInterest', vars: {} })
      }
      if (recommendation.id === 'missingVouchers' && missingAttachmentCount > 0) {
        why.push({ key: 'recWhyVoucherDeduction', vars: {} })
        why.push({ key: 'recWhyVoucherAudit', vars: {} })
      }
      if (recommendation.impactLbl === 'Endring' && costsYoy != null) {
        why.push({ key: 'recWhyCostGrowth', vars: { pct: Math.round(costsYoy) } })
      }
      return why.length ? { ...recommendation, why } : recommendation
    })
  }

  const buildAdvisorNarrative = ({
    income = 0,
    costs = 0,
    result = 0,
    previousIncome = 0,
    previousYearAvailable,
    costsYoy = null,
    concentration = null,
    home = {},
    forecast = [],
    buffer = 0,
    costsList = [],
    bench = [],
    recommendations = [],
    freeCapital = 0,
    voucherCoverageKnown = true,
    invoices = [],
    now = Date.now(),
    year = new Date(now).getFullYear(),
  } = {}) => {
    const salesOverdue = Math.max(0, num(home.overview?.salesOverdue))
    const salesCurrent = Math.max(0, num(home.overview?.salesCurrent))
    const purchasesOverdue = Math.max(0, num(home.overview?.purchasesOverdue))
    const purchasesCurrent = Math.max(0, num(home.overview?.purchasesCurrent))
    const vatDue = Math.max(0, num(home.publicFees?.vatDue))
    const taxBuffer = Math.max(0, num(home.publicFees?.taxBuffer))
    const missingAttachmentCount = Math.max(0, num(home.followup?.missingAttachmentCount))
    const flagged = safeArray(costsList).find((cost) => cost.flag) || null

    return {
      verdicts: {
        likviditet: liquidityVerdict({ forecast, buffer, salesOverdue, freeCapital }),
        kostnader: costVerdict({ costs, costsYoy, flagged }),
        skatt: taxVerdict({ vatDue, taxBuffer }),
        rapporter: reportVerdict({ income, costs, result }),
        bransje: benchmarkVerdict({ bench }),
      },
      why: {
        likviditet: liquidityWhy({ salesOverdue, purchasesOverdue, vatDue, taxBuffer }),
        kostnader: costWhy({ costs: costsList }),
      },
      liquidityWatch: liquidityWatch({ forecast, buffer, salesCurrent, purchasesCurrent }),
      moves: {
        likviditet: liquidityMoves({ salesOverdue, purchasesOverdue, vatDue, forecast, buffer, invoices, now, year }),
        kostnader: costMoves({ costs: costsList, missingAttachmentCount }),
      },
      confidence: confidenceFrom({
        income,
        costs,
        previousIncome,
        previousYearAvailable,
        concentration,
        receivables: salesCurrent + salesOverdue,
        payables: purchasesCurrent + purchasesOverdue,
        missingAttachmentCount,
        voucherCoverageKnown,
      }),
      monitor: monitorFrom({ forecast, buffer, salesOverdue, purchasesOverdue, vatDue, missingAttachmentCount, concentration }),
      recommendations: recommendationWhy({ recommendations, forecast, buffer, costsYoy, missingAttachmentCount }),
    }
  }

  return { buildAdvisorNarrative };
})();

Object.assign(globalThis, { PlummeAiAdvisorNarrative });
